import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./projects-videowall.css";

const completedStatuses = new Set(["completed", "closed", "cancelled"]);
const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
const clamp = (value) => Math.max(0, Math.min(100, Number(value) || 0));
const calendarDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return match ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 86400000 : null;
};
const formatDate = (value) => {
  const day = calendarDay(value);
  if (day === null) return "A definir";
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", timeZone: "UTC" }).format(new Date(day * 86400000)).replace(".", "");
};

export function projectWallMetrics(project, now = new Date()) {
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86400000;
  const start = calendarDay(project.startDate);
  const end = calendarDay(project.endDate);
  const hasTimeline = start !== null && end !== null && end >= start;
  const progress = clamp(Number(project.progress) > 0 || Number(project.planned) > 0
    ? project.progress
    : Number(project.commercial) > 0
      ? (Number(project.worked) / Number(project.commercial)) * 100
      : project.progress);
  const elapsed = hasTimeline ? clamp(((today - start + 1) / (end - start + 1)) * 100) : 0;
  const status = project.rawStatus === "paused"
    ? "paused"
    : hasTimeline && (today > end && progress < 100 || today >= start && elapsed > progress + 5)
      ? "late"
      : hasTimeline ? "on-time" : "unscheduled";
  return { ...project, progress: Math.round(progress), elapsed, status };
}

const statusLabels = { late: "Atrasado", paused: "Paralisado", "on-time": "Em dia", unscheduled: "Sem cronograma" };
const statusOrder = { late: 0, paused: 1, unscheduled: 2, "on-time": 3 };

function ProjectWallCard({ project }) {
  return (
    <article className={`projects-wall-card is-${project.status}`}>
      <div className="projects-wall-card-top">
        <div className="projects-wall-card-title">
          <span className="projects-wall-number">{project.projectNumber ? `PROJETO ${project.projectNumber}` : "PROJETO"}</span>
          <h3 title={project.name}>{project.name}</h3>
        </div>
        <span className={`projects-wall-status is-${project.status}`}><i />{statusLabels[project.status]}</span>
      </div>
      <p className="projects-wall-scope" title={[project.client, project.unit].filter(Boolean).join(" · ")}>
        {project.client}{project.unit ? ` · ${project.unit}` : ""}
      </p>
      <div className="projects-wall-progress-heading"><span>Avanço da obra</span><strong>{project.progress}%</strong></div>
      <div className="projects-wall-timeline" role="img" aria-label={`${project.progress}% realizado${project.status !== "unscheduled" ? `; ${Math.round(project.elapsed)}% do período decorrido` : ""}`}>
        <span className="projects-wall-timeline-fill" style={{ width: `${project.progress}%` }} />
        {project.status !== "unscheduled" && <i className="projects-wall-today" style={{ left: `${project.elapsed}%` }} title={`Hoje: ${Math.round(project.elapsed)}% do período`} />}
      </div>
      <div className="projects-wall-dates"><span>Início <b>{formatDate(project.startDate)}</b></span><span>Hoje <b className="projects-wall-today-label">●</b></span><span>Entrega <b>{formatDate(project.endDate)}</b></span></div>
    </article>
  );
}

export default function ProjectsVideowall({ projects, token, onClose }) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  const [clock, setClock] = useState(() => new Date());
  const [liveProjects, setLiveProjects] = useState(projects);
  useEffect(() => setLiveProjects(projects), [projects]);
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch(`${apiBase}/projects`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" });
        if (!response.ok) return;
        const data = await response.json();
        if (active) setLiveProjects(data.map(item => ({
          id: item.id,
          projectNumber: item.projectNumber || "",
          projectMode: item.projectMode || (item.preliminarySchedule ? "implementation_study" : "project"),
          preliminary: Boolean(item.preliminarySchedule),
          name: item.name,
          client: item.clientName || "Sem cliente",
          unit: item.unitName || "",
          rawStatus: item.status,
          progress: item.scheduleProgress || 0,
          planned: Number(item.scheduleDays) || 0,
          commercial: Number(item.commercialEstimatedDays) || 0,
          worked: Number(item.workedDays) || 0,
          startDate: item.scheduleStartDate || item.startDate || null,
          endDate: item.scheduleEndDate || item.endDate || null,
        })));
      } catch { /* Keep the last snapshot on a temporary connection failure. */ }
    };
    const timer = window.setInterval(refresh, 30000);
    return () => { active = false; window.clearInterval(timer); };
  }, [token]);
  useEffect(() => {
    const resize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    const keys = (event) => { if (event.key === "Escape") onClose(); };
    const fullscreen = () => { if (!document.fullscreenElement) onClose(); };
    window.addEventListener("resize", resize);
    window.addEventListener("keydown", keys);
    document.addEventListener("fullscreenchange", fullscreen);
    const clockTimer = window.setInterval(() => setClock(new Date()), 60000);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", keys);
      document.removeEventListener("fullscreenchange", fullscreen);
      window.clearInterval(clockTimer);
    };
  }, [onClose]);
  const allProjects = useMemo(() => liveProjects
    .filter(project => !completedStatuses.has(project.rawStatus) && project.projectMode !== "implementation_study" && !project.preliminary)
    .map(project => projectWallMetrics(project, clock))
    .sort((a, b) => statusOrder[a.status] - statusOrder[b.status] || String(a.endDate || "9999").localeCompare(String(b.endDate || "9999")) || String(a.name).localeCompare(String(b.name), "pt-BR")), [liveProjects, clock]);
  const columns = size.width >= 1450 ? 3 : size.width >= 850 ? 2 : 1;
  const rows = Math.max(1, Math.floor((size.height - (size.width < 850 ? 300 : 285)) / 178));
  const pageSize = columns * rows;
  const pages = Math.max(1, Math.ceil(allProjects.length / pageSize));
  const currentPage = Math.min(page, pages - 1);
  useEffect(() => {
    if (pages < 2) return undefined;
    const timer = window.setTimeout(() => setPage(value => (value + 1) % pages), 10000);
    return () => window.clearTimeout(timer);
  }, [pages, currentPage]);
  const counts = allProjects.reduce((result, project) => ({ ...result, [project.status]: result[project.status] + 1 }), { late: 0, paused: 0, "on-time": 0, unscheduled: 0 });
  const visible = allProjects.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const goTo = (direction) => setPage(value => (value + pages + direction) % pages);
  return createPortal(
    <div className="projects-videowall" style={{ "--wall-columns": columns, "--wall-rows": rows }}>
      <header className="projects-wall-header">
        <div className="projects-wall-brand"><img src="/aboutplan-logo-white-hq.png" alt="AboutPlan" /><span /> <div><small>VISÃO EXECUTIVA</small><h1>Projetos em andamento</h1></div></div>
        <div className="projects-wall-header-right"><span>Atualizado em {clock.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })} · {clock.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span><button type="button" onClick={onClose}>Sair do videowall</button></div>
      </header>
      <div className="projects-wall-summary">
        <div><span>Projetos ativos</span><strong>{allProjects.length}</strong></div>
        <div className="on-time"><span>Em dia</span><strong>{counts["on-time"]}</strong></div>
        <div className="late"><span>Atrasados</span><strong>{counts.late}</strong></div>
        <div className="paused"><span>Paralisados</span><strong>{counts.paused}</strong></div>
        <div className="unscheduled"><span>Sem cronograma</span><strong>{counts.unscheduled}</strong></div>
      </div>
      <main className="projects-wall-grid">
        {visible.map(project => <ProjectWallCard key={project.id} project={project} />)}
        {!allProjects.length && <div className="projects-wall-empty">Nenhum projeto em aberto no momento.</div>}
      </main>
      <footer className="projects-wall-footer"><span><i className="projects-wall-legend-fill" /> Avanço realizado <i className="projects-wall-legend-marker" /> Hoje no prazo</span><div className="projects-wall-pager">{pages > 1 && <button type="button" onClick={() => goTo(-1)} aria-label="Página anterior">‹</button>}<span>Página {currentPage + 1} de {pages}</span>{pages > 1 && <button type="button" onClick={() => goTo(1)} aria-label="Próxima página">›</button>}</div><span className="projects-wall-cycle">{pages > 1 ? "Próxima página em 10 s" : "Todos os projetos nesta tela"}</span></footer>
      {pages > 1 && <div key={currentPage} className="projects-wall-page-timer" />}
    </div>,
    document.body,
  );
}
